import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../../api/axiosInstance';

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
};

// ─── Toolbar helpers ──────────────────────────────────────────────────────────

const ToolBtn = ({ icon, label, title, onCmd }) => (
  <button
    type="button"
    title={title}
    onMouseDown={e => { e.preventDefault(); onCmd(); }}
    style={{
      minWidth: 28, height: 28, padding: '0 5px', borderRadius: 6,
      border: 'none', cursor: 'pointer', background: 'transparent',
      color: COLORS.gray, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 13, gap: 3, fontWeight: 600,
      transition: 'background 0.15s, color 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = COLORS.gray; }}
  >
    {icon && <i className={`ti ${icon}`} style={{ fontSize: 15 }} />}
    {label && <span style={{ fontSize: 12 }}>{label}</span>}
  </button>
);

const Sep = () => (
  <div style={{ width: 1, height: 18, background: '#dde3eb', margin: '0 3px', flexShrink: 0 }} />
);

// ─── Main component ───────────────────────────────────────────────────────────

const TermCodition = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [title, setTitle]                   = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [previewHtml, setPreviewHtml]       = useState('');

  // ── Fetch ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await axiosInstance.get('getTermsCondition/1');
        if (res.data?.success && res.data?.data) {
          const t = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
          if (t) {
            setTitle(t.title || '');
            setInitialContent(t.content || '');
            setPreviewHtml(t.content || '');
          }
        } else {
          Swal.fire('Error', res.data?.message || 'Failed to fetch terms', 'error');
        }
      } catch (e) {
        console.error(e);
        Swal.fire('Error', 'Failed to fetch terms and conditions', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  // Set editor content after the editor div mounts (after loading = false)
  useEffect(() => {
    if (!loading && editorRef.current) {
      editorRef.current.innerHTML = initialContent;
      setPreviewHtml(initialContent);
    }
  }, [loading]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── execCommand wrapper ───────────────────────────────────────────────────────
  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    syncPreview();
  };

  const syncPreview = () => {
    if (editorRef.current) setPreviewHtml(editorRef.current.innerHTML);
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const content = editorRef.current?.innerHTML || '';
    if (!title.trim() || !content.trim()) {
      Swal.fire('Error', 'Title and content are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.put('updateTermsCondition/1', { title, content });
      if (!res.data?.success) throw new Error(res.data?.message || 'Failed to update');
      setInitialContent(content);
      setPreviewHtml(content);
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Terms and conditions updated.', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', 'Failed to save. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <div className="spinner-border" role="status" style={{ color: COLORS.blue }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray; }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.orange}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-file-text" style={{ color: COLORS.orange, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Terms &amp; Conditions</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>This text will be printed on invoices</div>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Title field */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Payment Terms"
                style={{
                  width: '100%', height: 36, borderRadius: 8, border: '1px solid #dde3eb',
                  padding: '0 12px', fontSize: 13, color: '#1a202c', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = COLORS.blue}
                onBlur={e => e.target.style.borderColor = '#dde3eb'}
              />
            </div>

            {/* Editor */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                Content
              </label>

              <div style={{ border: '1px solid #dde3eb', borderRadius: 10, overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                  padding: '6px 10px', background: '#fafbfc', borderBottom: '1px solid #e8ecf0',
                }}>
                  <ToolBtn icon="ti-bold"          title="Bold"            onCmd={() => exec('bold')} />
                  <ToolBtn icon="ti-italic"        title="Italic"          onCmd={() => exec('italic')} />
                  <ToolBtn icon="ti-underline"     title="Underline"       onCmd={() => exec('underline')} />
                  <ToolBtn icon="ti-strikethrough" title="Strikethrough"   onCmd={() => exec('strikeThrough')} />
                  <Sep />
                  <ToolBtn icon="ti-list-numbers"  title="Ordered List"    onCmd={() => exec('insertOrderedList')} />
                  <ToolBtn icon="ti-list"          title="Unordered List"  onCmd={() => exec('insertUnorderedList')} />
                  <Sep />
                  <ToolBtn icon="ti-align-left"    title="Align Left"      onCmd={() => exec('justifyLeft')} />
                  <ToolBtn icon="ti-align-center"  title="Align Center"    onCmd={() => exec('justifyCenter')} />
                  <ToolBtn icon="ti-align-right"   title="Align Right"     onCmd={() => exec('justifyRight')} />
                  <Sep />
                  <ToolBtn label="H1" title="Heading 1"  onCmd={() => exec('formatBlock', 'h3')} />
                  <ToolBtn label="H2" title="Heading 2"  onCmd={() => exec('formatBlock', 'h4')} />
                  <ToolBtn label="¶"  title="Paragraph"  onCmd={() => exec('formatBlock', 'p')} />
                  <Sep />
                  <ToolBtn icon="ti-arrow-back-up"    title="Undo" onCmd={() => exec('undo')} />
                  <ToolBtn icon="ti-arrow-forward-up" title="Redo" onCmd={() => exec('redo')} />
                  <Sep />
                  <ToolBtn icon="ti-eraser" title="Clear Formatting" onCmd={() => exec('removeFormat')} />
                </div>

                {/* Editable area */}
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncPreview}
                  style={{
                    minHeight: 220, padding: '14px 16px', fontSize: 13,
                    color: '#1a202c', outline: 'none', lineHeight: 1.7,
                    background: '#fff',
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 5 }}>
                Use the toolbar above to format the text. Supports headings, lists, bold, italic, and alignment.
              </div>
            </div>

            {/* Preview */}
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
              }}>
                <i className="ti ti-eye" style={{ color: COLORS.gray, fontSize: 14 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Invoice Preview
                </span>
              </div>
              <div style={{
                border: '1px dashed #dde3eb', borderRadius: 10, padding: '16px 18px',
                background: '#fafbfc', minHeight: 80,
              }}>
                {title && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 8 }}>{title}</div>
                )}
                {previewHtml ? (
                  <div
                    style={{ fontSize: 12, color: COLORS.gray, lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                ) : (
                  <div style={{ fontSize: 12, color: '#c8d0da', fontStyle: 'italic' }}>
                    Start typing to see a preview…
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* ── Footer ── */}
          <div style={{
            padding: '12px 20px', borderTop: '1px solid #e8ecf0',
            background: '#fafbfc', display: 'flex', justifyContent: 'flex-end',
          }}>
            <button
              type="button"
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '6px 20px' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <i className="ti ti-loader-2" style={{ fontSize: 14 }} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermCodition;
